import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Trophy,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Mail,
  Phone,
  MapPin,
  CheckCircle
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { lang } = useParams();
  const { t, i18n } = useTranslation('common');

  // Get current language from URL or i18n
  const currentLang = lang || i18n.language || 'en';

  const socialLinks = [
    { icon: Facebook, url: '#', label: 'Facebook' },
    { icon: Twitter, url: '#', label: 'Twitter' },
    { icon: Instagram, url: '#', label: 'Instagram' },
    { icon: Youtube, url: '#', label: 'YouTube' },
  ];

  const quickLinks = [
    { label: t('footer.quickLinks.aboutUs'), path: `/${currentLang}/about` },
    { label: t('footer.quickLinks.awardCategories'), path: `/${currentLang}/nominations#categories` },
    { label: t('footer.quickLinks.howToApply'), path: `/${currentLang}/nominations` },
    { label: t('footer.quickLinks.pastWinners'), path: `/${currentLang}/winners` },
  ];

  return (
    <Box component="footer" sx={{ backgroundColor: '#000000', color: 'white' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 5 } }}>
        <Grid container spacing={3}>
          {/* Brand Section */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Trophy size={36} style={{ color: '#D4A747', marginRight: 10 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                    {t('footer.brandName')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#D4A747',
                      textTransform: 'uppercase',
                      letterSpacing: 1.2
                    }}
                  >
                    {t('footer.brandSubtitle')}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, fontSize: '0.8rem' }}>
                {t('footer.brandDescription')}
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                  {t('footer.followUs')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <IconButton
                          component="a"
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.label}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(212, 167, 71, 0.1)',
                            color: '#D4A747',
                            border: '1px solid rgba(212, 167, 71, 0.3)',
                            '&:hover': {
                              backgroundColor: '#D4A747',
                              color: 'black',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Icon size={18} />
                        </IconButton>
                      </motion.div>
                    );
                  })}
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Quick Links */}
          <Grid size={{ xs: 6, md: 3, lg: 2 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
                {t('footer.quickLinks.title')}
              </Typography>
              <List dense sx={{ py: 0 }}>
                {quickLinks.map((link) => (
                  <ListItem
                    key={link.path}
                    component={Link}
                    to={link.path}
                    sx={{
                      px: 0,
                      py: 0.3,
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#D4A747',
                      },
                      transition: 'color 0.3s ease',
                    }}
                  >
                    <ListItemText primary={link.label} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Grid>

          {/* Award Categories */}
          <Grid size={{ xs: 6, md: 3, lg: 2 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
                {t('footer.categories.title')}
              </Typography>
              <List dense sx={{ py: 0 }}>
                {['Farmer', 'FPO', 'Innovator'].map((category) => (
                  <ListItem
                    key={category}
                    component={Link}
                    to={`/${currentLang}/nominations#categories`}
                    sx={{
                      px: 0,
                      py: 0.3,
                      textDecoration: 'none',
                      '&:hover .MuiListItemText-primary': {
                        color: '#D4A747',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 22 }}>
                      <CheckCircle size={13} style={{ color: '#D4A747' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={category}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: {
                          color: 'rgba(255, 255, 255, 0.7)',
                          transition: 'color 0.3s ease',
                          fontSize: '0.8rem',
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Grid>

          {/* Contact */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
                {t('footer.contact.title')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Mail size={16} style={{ color: '#D4A747', marginRight: 8, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
                    {t('footer.contact.email')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone size={16} style={{ color: '#D4A747', marginRight: 8, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
                    {t('footer.contact.phone')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <MapPin size={16} style={{ color: '#D4A747', marginRight: 8, marginTop: 2, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
                    {t('footer.contact.address')}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 3 }} />
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: { xs: 'center', md: 'left' }, fontSize: '0.75rem' }}>
            © {currentYear} {t('footer.copyright')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 }, alignItems: 'center' }}>
            {[
              { label: t('footer.legal.privacyPolicy'), path: `/${currentLang}/privacy-policy` },
              { label: t('footer.legal.termsConditions'), path: `/${currentLang}/terms-and-conditions` },
            ].map((link) => (
              <Typography
                key={link.path}
                component={Link}
                to={link.path}
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  '&:hover': {
                    color: '#D4A747',
                  },
                  transition: 'color 0.3s ease',
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
